/**
 * @file OverviewOpenOrdersSection.tsx
 * @description «Сейчас в работе» на «Обзоре» клиента (phase19.31, SC68):
 * только открытые заказы — номер, стадия пилюлей, чей ход, доставка с
 * трек-номером и сумма; «Все заказы →» ведёт на вкладку «Заказы».
 *
 * Строка — ссылка на карточку заказа высотой от 48px; на 360 вторая линия
 * (доставка, трек) переносится, сумма не уезжает за край. Стадия — тон по
 * виду стадии, как в реестре заказов; «чей ход» — тот же стикер `OwnerChip`.
 */

import { Link } from "react-router";

import { OwnerChip } from "../../orders/OwnerChip";
import { SectionCard } from "../../ui/SectionCard";
import { StatusPill } from "../../ui/StatusPill";
import type { CustomerOverviewOrderDto } from "../../../lib/api-customers-overview";
import { formatRub } from "../../../lib/crm-deal-view";
import { ORDER_STAGE_KIND_STATUS_TONE } from "../../../lib/order-registry-cells";

interface Props {
  orders: CustomerOverviewOrderDto[];
  total: number;
  orderHref: (orderId: string) => string;
}

const ROW_CLASS =
  "flex min-h-12 items-start gap-3 rounded-lg px-2 py-2 outline-none transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-ring";

export function OverviewOpenOrdersSection({ orders, total, orderHref }: Props) {
  return (
    <SectionCard title="Сейчас в работе" link={{ label: "Все заказы", to: "?tab=orders" }}>
      {orders.length === 0 ? (
        <p className="type-body text-on-surface-variant">Открытых заказов нет</p>
      ) : (
        <ul aria-label="Открытые заказы клиента" className="-mx-2 flex flex-col">
          {orders.map((o) => (
            <li key={o.id}>
              <Link to={orderHref(o.id)} className={ROW_CLASS}>
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="type-body font-medium tabular-nums text-on-surface">{o.number}</span>
                    <StatusPill tone={o.stageKind ? ORDER_STAGE_KIND_STATUS_TONE[o.stageKind] : "neutral"}>{o.stageName}</StatusPill>
                    {o.turn ? <OwnerChip owner={o.turn} /> : null}
                  </span>
                  {o.deliveryText || o.trackNumber ? (
                    <span className="type-caption break-words text-on-surface-variant">
                      {o.deliveryText}
                      {o.deliveryText && o.trackNumber ? " · " : null}
                      {o.trackNumber ? (
                        <>
                          трек <span className="font-mono">{o.trackNumber}</span>
                        </>
                      ) : null}
                    </span>
                  ) : null}
                </span>
                <span className="type-body shrink-0 whitespace-nowrap font-semibold tabular-nums text-on-surface">
                  {formatRub(o.totalGross, "whole")}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {total > orders.length ? (
        <p className="type-caption text-on-surface-variant">
          Показаны {orders.length} из {total} открытых
        </p>
      ) : null}
    </SectionCard>
  );
}
